import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "fixed inset-0 z-[9999] overflow-hidden"
}
const _hoisted_2 = { class: "text-2xl text-[#eaf2ef] drop-shadow-[0_0_3px_#a8c3b4]" }
const _hoisted_3 = { class: "absolute inset-0 z-[10000] pointer-events-none" }
const _hoisted_4 = ["src", "alt"]

import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useImageViewer } from '../composables/useImageViewer';
const headerHeight = 48;
const pagePadding = 32;
const expandedCornerRadius = 28;
const collapsedCornerRadius = 20;


export default /*@__PURE__*/_defineComponent({
  __name: 'FullScreenImage',
  setup(__props) {

const {
    imageViewIsOpen,
    imageSrc,
    imageAltText,
    imageTitle,
    imageSourceRect,
    viewerPhase,
    closeImageMenu,
    markImageMenuOpen,
    finalizeImageMenuClose,
} = useImageViewer();

const isExpanded = ref(false);
const viewportWidth = ref(window.innerWidth);
const viewportHeight = ref(window.innerHeight);
const handleResize = () => {
    viewportWidth.value = window.innerWidth;
    viewportHeight.value = window.innerHeight;
};

const sourceStyle = computed(() => {
    const sourceRect = imageSourceRect.value;

    if (!sourceRect) {
        return {
            top: '50%',
            left: '50%',
            width: '1px',
            height: '1px',
            transform: 'translate(-50%, -50%)',
            borderRadius: `${collapsedCornerRadius}px`,
            opacity: '0',
        };
    }

    return {
        top: `${sourceRect.top}px`,
        left: `${sourceRect.left}px`,
        width: `${sourceRect.width}px`,
        height: `${sourceRect.height}px`,
        transform: 'translate(0, 0)',
        borderRadius: `${collapsedCornerRadius}px`,
        opacity: '1',
    };
});

const targetStyle = computed(() => {
    const sourceRect = imageSourceRect.value;

    if (!sourceRect) {
        return {
            top: `${headerHeight + pagePadding}px`,
            left: `${pagePadding}px`,
            width: `${viewportWidth.value - pagePadding * 2}px`,
            height: `${viewportHeight.value - headerHeight - pagePadding * 2}px`,
            transform: 'translate(0, 0)',
            borderRadius: `${expandedCornerRadius}px`,
            opacity: '1',
        };
    }

    const aspectRatio = sourceRect.width / sourceRect.height;
    const availableWidth = Math.max(240, viewportWidth.value - pagePadding * 2);
    const availableHeight = Math.max(240, viewportHeight.value - headerHeight - pagePadding * 2);

    let width = availableWidth;
    let height = width / aspectRatio;

    if (height > availableHeight) {
        height = availableHeight;
        width = height * aspectRatio;
    }

    const top = headerHeight + pagePadding + (availableHeight - height) / 2;
    const left = (viewportWidth.value - width) / 2;

    return {
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`,
        height: `${height}px`,
        transform: 'translate(0, 0)',
        borderRadius: `${expandedCornerRadius}px`,
        opacity: '1',
    };
});

const cardStyle = computed(() => ({
    ...(isExpanded.value ? targetStyle.value : sourceStyle.value),
}));

watch(
    viewerPhase,
    async (phase) => {
        if (phase === 'opening') {
            isExpanded.value = false;
            await nextTick();
            requestAnimationFrame(() => {
                isExpanded.value = true;
            });
            return;
        }

        if (phase === 'closing') {
            isExpanded.value = false;
            return;
        }

        if (phase === 'closed') {
            isExpanded.value = false;
        }
    },
    { immediate: true }
);

onMounted(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});

const handleTransitionEnd = (event: TransitionEvent) => {
    if (!['top', 'left', 'width', 'height', 'transform', 'opacity'].includes(event.propertyName)) {
        return;
    }

    if (viewerPhase.value === 'opening' && isExpanded.value) {
        markImageMenuOpen();
        return;
    }

    if (viewerPhase.value === 'closing' && !isExpanded.value) {
        finalizeImageMenuClose();
    }
};

return (_ctx: any,_cache: any) => {
  return (_unref(imageViewIsOpen) && _unref(imageSrc) && _unref(imageAltText))
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", {
          class: _normalizeClass(["absolute inset-0 bg-[rgba(0,0,0,0.72)] backdrop-blur-lg transition-opacity duration-[500ms] ease-[cubic-bezier(0.22,1,0.36,1)]", isExpanded.value ? 'opacity-100' : 'opacity-0'])
        }, null, 2),
        _createElementVNode("div", {
          class: _normalizeClass(["absolute inset-x-0 top-0 z-10 h-12 shrink-0 bg-[rgba(0,0,0,0.7)] backdrop-blur-lg flex flex-row justify-between items-center px-4 transition-opacity duration-[500ms] ease-[cubic-bezier(0.22,1,0.36,1)]", isExpanded.value ? 'opacity-100' : 'opacity-0'])
        }, [
          _createElementVNode("span", _hoisted_2, _toDisplayString(_unref(imageTitle) ?? _unref(imageAltText)), 1),
          _createElementVNode("button", {
            class: "hamburger block relative flex items-center justify-center w-12 h-12 -mr-4 grid",
            "aria-label": "Close image viewer",
            onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(closeImageMenu) && _unref(closeImageMenu)(...args)))
          }, _cache[1] || (_cache[1] = [
            _createElementVNode("span", { class: "hamburger-item w-8 h-[3px] absolute bg-[#eaf2ef] drop-shadow-[0_0_3px_#a8c3b4] rounded-md rotate-45" }, null, -1),
            _createElementVNode("span", { class: "w-8 h-[3px] absolute bg-[#eaf2ef] drop-shadow-[0_0_3px_#a8c3b4] rounded-md -rotate-45" }, null, -1)
          ]))
        ], 2),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", {
            class: "viewer-card fixed overflow-hidden transition-[top,left,width,height,transform,border-radius,opacity] duration-[500ms] ease-[cubic-bezier(0.22,1,0.36,1)]",
            style: _normalizeStyle(cardStyle.value),
            onTransitionend: handleTransitionEnd
          }, [
            _createElementVNode("img", {
              src: _unref(imageSrc),
              alt: _unref(imageAltText),
              class: "viewer-image"
            }, null, 8, _hoisted_4)
          ], 36)
        ])
      ]))
    : _createCommentVNode("", true)
}
}

})